/* Per-user allowed login countries (empty = no restriction). */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, getAuthHeader } from "@/App";
import { Button } from "@/components/ui/button";

const CODES = ("af ax al dz as ad ao ai aq ag ar am aw au at az bs bh bd bb by be bz bj bm bt bo ba bw br io bn bg bf bi kh cm ca cv ky cf td cl cn cx cc co km cg cd ck cr ci hr cu cy cz dk dj dm do ec eg sv gq er ee et fk fo fj fi fr gf pf ga gm ge de gh gi gr gl gd gp gu gt gg gn gw gy ht va hn hk hu is in id ir iq ie im il it jm jp je jo kz ke ki kp kr kw kg la lv lb ls lr ly li lt lu mo mk mg mw my mv ml mt mh mq mr mu yt mx fm md mc mn me ms ma mz mm na nr np nl nc nz ni ne ng nu nf mp no om pk pw ps pa pg py pe ph pn pl pt pr qa re ro ru rw sh kn lc pm vc ws sm st sa sn rs sc sl sg sk si sb so za es lk sd sr sj sz se ch sy tw tj tz th tl tg tk to tt tn tr tm tc tv ug ua ae gb us uy uz vu ve vn vg vi wf eh ye zm zw").split(" ");
const names = new Intl.DisplayNames(["en"], { type: "region" });
const nameOf = (c) => { try { return names.of(c.toUpperCase()); } catch { return c.toUpperCase(); } };
const SORTED = [...CODES].sort((a, b) => nameOf(a).localeCompare(nameOf(b)));

export const AllowedCountries = ({ userId, initial }) => {
  const [list, setList] = useState(initial);
  const [busy, setBusy] = useState(false);
  const save = async (next) => {
    setBusy(true);
    try {
      const r = await axios.put(`${API}/admin/users/${userId}/allowed-countries`, { countries: next }, { headers: getAuthHeader() });
      setList(r.data.allowed_countries);
      toast.success(next.length ? "Allowed countries saved" : "Country restriction removed");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  return (
    <section data-testid="allowed-countries">
      <h4 className="font-semibold text-slate-700 mb-1">Allowed login countries</h4>
      <p className="text-[11px] text-slate-500 mb-1.5">
        {list.length ? "Logins from any other country are blocked and admins are alerted. Unknown locations are allowed but flagged in Security."
          : "No restriction — this user can log in from any country."}
      </p>
      <div className="flex flex-wrap items-center gap-1.5">
        {list.map(c => (
          <span key={c} className="inline-flex items-center gap-1 rounded-full border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-emerald-800" data-testid={`allowed-country-${c}`}>
            <img src={`https://flagcdn.com/16x12/${c}.png`} alt="" width={16} height={12} /> {nameOf(c)}
            <button type="button" disabled={busy} onClick={() => save(list.filter(x => x !== c))} className="ml-0.5 text-emerald-700 hover:text-red-600"
              data-testid={`allowed-country-remove-${c}`} aria-label={`Remove ${nameOf(c)}`}>✕</button>
          </span>
        ))}
        <select value="" disabled={busy} onChange={(e) => e.target.value && save([...list, e.target.value])}
          className="h-7 rounded border border-slate-300 bg-white px-1 text-xs" data-testid="allowed-country-add">
          <option value="">+ Add country…</option>
          {SORTED.filter(c => !list.includes(c)).map(c => <option key={c} value={c}>{nameOf(c)}</option>)}
        </select>
        {list.length > 0 && (
          <Button size="sm" variant="ghost" className="h-6 px-2 text-red-600" disabled={busy} onClick={() => save([])} data-testid="allowed-country-clear">Clear all</Button>
        )}
      </div>
    </section>
  );
};
